import {
    UDEF_WINDOW_EMBODIED_BY_GLAZING_AND_FRAME,
    UDEF_WINDOW_EMISSION_TIERS,
    WINDOW_DOUBLE_GLAZING_TYPES,
    WINDOW_TRIPLE_GLAZING_TYPES,
} from "./EMBC_UDEF_MAPS";

const WINDOW_DOUBLE_GLAZING_SET = new Set(WINDOW_DOUBLE_GLAZING_TYPES);
const WINDOW_TRIPLE_GLAZING_SET = new Set(WINDOW_TRIPLE_GLAZING_TYPES);

/** @returns {{ emissions: number, storage: number } | null} */
export function lookupUdefWindowEmbodiedDefaults(glazingTypeId, frameMaterialValue) {
    const g = (glazingTypeId ?? "1").toString();
    const f = (frameMaterialValue ?? "0").toString();
    let glazingCategory;
    if (WINDOW_DOUBLE_GLAZING_SET.has(g)) glazingCategory = "double";
    else if (WINDOW_TRIPLE_GLAZING_SET.has(g)) glazingCategory = "triple";
    else return null;
    
    return UDEF_WINDOW_EMBODIED_BY_GLAZING_AND_FRAME[glazingCategory]?.[f] ?? null;
}

// -----------------------------------------------------------------------------
// Whole-window embodied carbon (glazing × frame lookup + optional `layers.wholeUdefWindow` EPD)
// -----------------------------------------------------------------------------

export function getUdefWindowEmissionGrade(emissions) {
    if (emissions == null || !Number.isFinite(emissions)) return "N/A";
    if (emissions === 0) return "N/A";
    for (const [maxE, grade] of UDEF_WINDOW_EMISSION_TIERS) {
        if (emissions <= maxE) return grade;
    }
    return "D";
}

/** Coarse storage grades for whole-window (wood-frame variants carry biogenic storage). */
export function getUdefWindowStorageGrade(storage) {
    if (storage == null || !Number.isFinite(storage)) return "N/A";
    if (storage >= 16) return "A";
    if (storage >= 8) return "B";
    return "N/A";
}

/** User EPD / overrides for whole-window embodied carbon, stored on `layers.wholeUdefWindow`. */
export const WINDOW_UDEF_EMB_KEY = "wholeUdefWindow";

const UDEF_WHOLE_WINDOW_LAYER_LABELS = {
    window: "Whole Window: Overall Characteristics",
    windowOverall: "Whole Window: Overall Characteristics",
    windowLegacy: "Whole Window: Legacy Inputs",
};

/** Display label for a UDEF whole-window layer row in embodied summaries. */
export function getUdefWindowLayerDisplayLabel(layerType) {
    return UDEF_WHOLE_WINDOW_LAYER_LABELS[layerType] ?? "Whole Window";
}

export function getWindowLayersSnapshotForUdefEmbC(layers) {
    if (!layers) return {};
    return {
        glazingTypeId: layers.glazingType?.id,
        frameMaterialValue: layers.frameMaterial?.value,
    };
}

export function udefWindowEmbodiedMismatch(layers, udefWindowC) {
    if (!udefWindowC || udefWindowC.emissions == null) return false;
    const cur = getWindowLayersSnapshotForUdefEmbC(layers);
    
    return (
        String(udefWindowC.glazingTypeId ?? "") !== String(cur.glazingTypeId ?? "") ||
        String(udefWindowC.frameMaterialValue ?? "") !== String(cur.frameMaterialValue ?? "")
    );
}

/** No map row (e.g. unsupported glazing/frame): show 0 kg/m² like other “no contribution” rows, not NaN. */
const WINDOW_UNCALCULATED = () => ({
    emissions: 0,
    storage: 0,
    emissionGrade: "N/A",
    storageGrade: "N/A",
});

export const getUdefWindowCodeEmbodiedCarbon = (layers) => {
    if (!layers) {
        return WINDOW_UNCALCULATED();
    }

    const glazingTypeId = (layers?.glazingType?.id || "0").toString();
    const frameMaterialValue = (layers?.frameMaterial?.value || "0").toString();

    const row = lookupUdefWindowEmbodiedDefaults(glazingTypeId, frameMaterialValue);
    if (row == null || row.emissions == null) {
        return WINDOW_UNCALCULATED();
    }

    const emissionsData = {
        emissions: row.emissions,
        storage: row.storage,
        emissionGrade: "N/A",
        storageGrade: "N/A",
    };
    emissionsData.emissionGrade = getUdefWindowEmissionGrade(emissionsData.emissions);
    emissionsData.storageGrade = getUdefWindowStorageGrade(emissionsData.storage);
    return emissionsData;
};

/** First window layer from stored uDef window code (legacy before overall, matching the code drawer). */
export function getUdefWindowLayerFromCode(codeData = {}) {
    const { window: windowOverallLayers = {}, windowLegacy: windowLegacyLayers = {} } = codeData.layers || {};

    const legacy = Object.keys(windowLegacyLayers).map((key) => ({
        ...windowLegacyLayers[key],
        id: key,
        type: "windowLegacy",
    }));
    const overall = Object.keys(windowOverallLayers).map((key) => ({
        ...windowOverallLayers[key],
        id: key,
        type: "windowOverall",
    }));

    const codeLayers = [...legacy, ...overall];
    return codeLayers[0] || null;
}

/**
 * Display values for whole-window embodied carbon (map defaults + optional `layers.wholeUdefWindow` overrides).
 */
export function getUdefWindowCodeEmbodiedCarbonDisplay({ layers }) {
    const defaults = getUdefWindowCodeEmbodiedCarbon(layers);
    const udefWindowC = layers?.[WINDOW_UDEF_EMB_KEY];

    if (udefWindowC != null && udefWindowC.emissions != null) {
        const emissions = udefWindowC.emissions;
        const storage = udefWindowC.storage ?? 0;
        return {
            emissions,
            storage,
            emissionGrade: getUdefWindowEmissionGrade(emissions),
            storageGrade: getUdefWindowStorageGrade(storage),
            defaultSource: "",
            hasMismatch: udefWindowEmbodiedMismatch(layers, udefWindowC),
        };
    }
    return {
        emissions: defaults.emissions,
        storage: defaults.storage,
        emissionGrade: defaults.emissionGrade,
        storageGrade: defaults.storageGrade,
        defaultSource: "",
        hasMismatch: false,
    };
}